import React, { useEffect, useRef, useContext, useState } from 'react'
import styled from 'styled-typed'
import { History } from 'history'
import { Navigation } from 'navigation'
import { match as RouteMatch } from 'react-router-dom'
import { delay } from 'utils'
import { format, isAfter, isBefore, parse } from 'date-fns'
import { ActionButton, ActionButtonA } from 'uiComponents/buttons'
import { withNavigation } from 'hocs'
import { ButtonWrapper } from 'uiComponents/pageElements'
import { withFeatures } from 'features'
import { MessageKind } from 'uiComponents/messages'
import { ContainerBody } from 'uiComponents/settingsContainer'
import { WeekdayPattern } from 'uiComponents/weekdayPattern'
import { TableLoader, ChartDataLoader } from 'uiComponents/loaders'
import { DatePicker } from 'uiComponents/popups/datePickerInput'
import { TextInput, NumberInput, TimePicker } from 'uiComponents/input'
import { Col, Row } from 'uiComponents/flex'
import { FormItem, FormItemName, ValidationMessage } from 'uiComponents/form/formElements'
import { InventoryServiceContext } from 'inventory/context'
import { InventoryRule } from 'inventory/inventoryService'
import Infotip from 'uiComponents/infotip'
import { DateFormats, parseTime } from 'utils/dates'
import { parseDate } from 'utils'
import { useTranslation } from 'translations'

const Section = styled(Col)`
    margin-bottom: 2em;
`

interface InventoryRuleFormParams {
    accountSlug: string
    ruleId: string
    inventoryId: string
}

interface InventoryRuleFormProps {
    accountSlug: string
    match: RouteMatch<InventoryRuleFormParams>
    history: History
    navigation: Navigation
    setActiveSection: (section: string, header: string) => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
    hasFeature: (feature: string, accountSlug: string) => boolean
}

function InventoryRuleForm(props: InventoryRuleFormProps) {
    const { t } = useTranslation()
    const didMountRef = useRef(false)
    const inventoryService = useContext(InventoryServiceContext)
    const [loading, setLoading] = useState<boolean>(false)
    const [updating, setUpdating] = useState<boolean>(false)
    const [name, setName] = useState<string | null>('')
    const [availability, setAvailability] = useState<number | null>(null)
    const [weekdays, setWeekdays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6])
    const [timeFrom, setTimeFrom] = useState<string>('00:00')
    const [timeTo, setTimeTo] = useState<string>('23:59')
    const [effectiveFrom, setEffectiveFrom] = useState<string | null>(null)
    const [effectiveTo, setEffectiveTo] = useState<string | null>(null)
    const [timeFromError, setTimeFromError] = useState<string | null>(null)
    const [timeToError, setTimeToError] = useState<string | null>(null)
    const [effectiveFromError, setEffectiveFromError] = useState<string | null>(null)
    const [effectiveToError, setEffectiveToError] = useState<string | null>(null)
    const [priority, setPriority] = useState<number | null>(2)
    const [confirmDeletion, setConfirmDeletion] = useState<boolean>(false)

    const fetchRuleData = async (inventoryId: string, ruleId: string) => {
        try {
            setLoading(true)
            const data = await inventoryService.getInventoryRuleDetails(props.accountSlug, inventoryId, ruleId)
            setDataToState(data)
        } catch {
            props.replaceMessages('server_error', 'error', t('inventory.ruleEditForm.fetchError'))
        } finally {
            setLoading(false)
        }
    }

    const setDataToState = (data: InventoryRule) => {
        setName(data.name)
        setAvailability(data.availability)
        setWeekdays(data.weekdays)
        setTimeFrom(data.timeFrom || '00:00')
        setTimeTo(data.timeTo || '23:59')
        setEffectiveFrom(data.effectiveFrom)
        setEffectiveTo(data.effectiveTo)
        setPriority(data.priority)
    }

    const validateTimes = () => {
        setTimeFromError(isAfter(parseTime(timeFrom), parseTime(timeTo)) ? t('inventory.ruleEditForm.timeFromError') : null)
        setTimeToError(isBefore(parseTime(timeTo), parseTime(timeFrom)) ? t('inventory.ruleEditForm.timeToError') : null)
    }

    const validateDates = () => {
        setEffectiveFromError(
            !effectiveFrom && effectiveTo
                ? t('inventory.ruleEditForm.bothDatesOrNeither')
                : effectiveFrom && effectiveTo && isAfter(new Date(effectiveFrom), new Date(effectiveTo))
                ? t('inventory.ruleEditForm.effectiveFromError')
                : null,
        )
        setEffectiveToError(
            effectiveFrom && !effectiveTo
                ? t('inventory.ruleEditForm.bothDatesOrNeither')
                : effectiveFrom && effectiveTo && isBefore(new Date(effectiveTo), new Date(effectiveFrom))
                ? t('inventory.ruleEditForm.effectiveToError')
                : null,
        )
    }

    useEffect(validateTimes, [timeFrom, timeTo])
    useEffect(validateDates, [effectiveFrom, effectiveTo])

    useEffect(() => {
        const ruleId = props.match.params.ruleId
        const inventoryId = props.match.params.inventoryId
        props.setActiveSection('inventory', ruleId === 'new' ? t('inventory.ruleEditForm.titleNew') : t('inventory.ruleEditForm.titleEdit'))
        if (inventoryId && ruleId && ruleId !== 'new') {
            fetchRuleData(inventoryId, ruleId)
        }
    }, [])

    useEffect(() => {
        if (didMountRef.current) {
            props.history.push(`/account/${props.accountSlug}/products/inventory/home`)
        } else {
            didMountRef.current = true
        }
    }, [props.accountSlug])

    const onCapacityChange = (value: string) => {
        value ? setAvailability(Number(value)) : setAvailability(null)
    }

    const isDataValid = () => {
        return !timeFromError && !timeToError && !effectiveFromError && !effectiveToError
    }

    const onSaveSuccess = () => {
        setUpdating(false)
        props.history.push(`/account/${props.accountSlug}/products/inventory/item/${props.match.params.inventoryId}`)
    }

    const onRuleSave = async () => {
        if (isDataValid()) {
            try {
                setUpdating(true)
                const inventoryId = props.match.params.inventoryId
                const ruleId = props.match.params.ruleId
                const payload = {
                    id: ruleId,
                    name: name ? name : null,
                    availability,
                    weekdays,
                    effectiveFrom,
                    effectiveTo,
                    timeFrom: timeFrom === '00:00' && timeTo === '23:59' ? null : timeFrom,
                    timeTo: timeFrom === '00:00' && timeTo === '23:59' ? null : timeTo,
                    priority: priority ? priority : 2,
                }
                if (ruleId !== 'new') {
                    await inventoryService.updateInventoryRule(props.accountSlug, inventoryId, ruleId, payload)
                    onSaveSuccess()
                } else {
                    await inventoryService.createInventoryRule(props.accountSlug, inventoryId, payload)
                    onSaveSuccess()
                }
                props.replaceMessages('success', 'success', t('inventory.ruleEditForm.saveSuccess'))
                await delay(3000)
                props.removeAllMessages()
            } catch {
                setUpdating(false)
                props.replaceMessages(
                    'server_error',
                    'error',
                    t('inventory.ruleEditForm.saveError'),
                )
            }
        } else {
            props.replaceMessages('validation_error', 'error', t('inventory.ruleEditForm.validationErrors'))
        }
    }

    const onDeleteRule = async () => {
        if (confirmDeletion) {
            try {
                const inventoryId = props.match.params.inventoryId
                await inventoryService.deleteInventoryRule(props.accountSlug, inventoryId, props.match.params.ruleId)
                props.history.replace(`/account/${props.accountSlug}/products/inventory/item/${inventoryId}`)
            } catch {
                props.replaceMessages('server_error', 'error', t('inventory.ruleEditForm.deleteError'))
            }
        } else {
            setConfirmDeletion(true)
        }
    }

    const formatTime = (time: string) => format(parse(time, 'HH:mm', new Date()), DateFormats.SHORT_TIME)
    const timeFromTooltipText = t('inventory.ruleEditForm.wholeDayTooltip', { time: formatTime('00:00') })
    const timeToTooltipTopText = t('inventory.ruleEditForm.wholeDayTooltip', { time: formatTime('23:59') })
    const timeToTooltipBottomText = t('inventory.ruleEditForm.endTimeNotInclusive', {
        from: formatTime('13:00'),
        to: formatTime('15:00'),
        inside: formatTime('14:30'),
    })

    return (
        <div style={{ position: 'relative' }}>
            {loading && <TableLoader />}
            {updating && <ChartDataLoader />}
            {!loading && (
                <ContainerBody id="inventory-rule-form" style={{ flexDirection: 'column' }}>
                    <Row>
                        <Section span={6}>
                            <FormItem htmlFor="rule-name" style={{ position: 'relative' }}>
                                <FormItemName>{t('inventory.ruleEditForm.nameLabel')}</FormItemName>
                                <TextInput
                                    id="rule-name"
                                    name="rule-name"
                                    type="text"
                                    maxLength={60}
                                    value={name ? name : ''}
                                    placeholder={t('inventory.ruleEditForm.namePlaceholder')}
                                    onChange={(e) => setName(e.target.value)}
                                    block
                                />
                            </FormItem>
                        </Section>
                        <Section span={6}>
                            <FormItem htmlFor="inventory-rule-pattern" style={{ position: 'relative' }}>
                                <FormItemName>{t('inventory.ruleEditForm.weeklyPatternLabel')}</FormItemName>
                                <WeekdayPattern
                                    id="inventory-rule-pattern"
                                    weekdays={weekdays.length > 0 ? weekdays.toString() : '0,1,2,3,4,5,6'}
                                    large
                                    handleWeekdayToggleChange={(wkds) => setWeekdays(wkds.map((wd) => Number(wd)))}
                                    type="strict"
                                />
                            </FormItem>
                        </Section>
                    </Row>
                    <Row>
                        <Section span={6}>
                            <Row>
                                <Col span={6}>
                                    <FormItem htmlFor="effectiveFrom" style={{ position: 'relative' }}>
                                        <FormItemName>{t('inventory.ruleEditForm.effectiveFromLabel')}</FormItemName>
                                        <DatePicker
                                            id="effectiveFrom"
                                            date={effectiveFrom ? parseDate(effectiveFrom) : null}
                                            onChange={(value: Date) =>
                                                setEffectiveFrom(value ? format(value, 'yyyy-MM-dd') : null)
                                            }
                                            status={effectiveFromError ? 'error' : 'normal'}
                                            allowNullDate
                                        />
                                        {effectiveFromError && (
                                            <ValidationMessage
                                                className="validation-message-visible"
                                                style={{ top: '0' }}
                                            >
                                                {effectiveFromError}
                                            </ValidationMessage>
                                        )}
                                    </FormItem>
                                </Col>
                                <Col span={6}>
                                    <FormItem htmlFor="effectiveTo" style={{ position: 'relative' }}>
                                        <FormItemName>
                                            {t('inventory.ruleEditForm.effectiveToLabel')}
                                            <Infotip pointer="left">{t('inventory.ruleEditForm.dateInclusive')}</Infotip>
                                        </FormItemName>
                                        <DatePicker
                                            id="effectiveTo"
                                            date={effectiveTo ? parseDate(effectiveTo) : null}
                                            onChange={(value: Date) =>
                                                setEffectiveTo(value ? format(value, 'yyyy-MM-dd') : null)
                                            }
                                            status={effectiveToError ? 'error' : 'normal'}
                                            allowNullDate
                                            lowerBoundary={effectiveFrom ? new Date(effectiveFrom) : null}
                                        />
                                        {effectiveToError && (
                                            <ValidationMessage
                                                className="validation-message-visible"
                                                style={{ top: '0' }}
                                            >
                                                {effectiveToError}
                                            </ValidationMessage>
                                        )}
                                    </FormItem>
                                </Col>
                            </Row>
                        </Section>
                        <Section span={6}>
                            <Row>
                                <Col span={6}>
                                    <FormItem htmlFor="timeFrom" style={{ position: 'relative' }}>
                                        <FormItemName>
                                            {t('inventory.ruleEditForm.timeFromLabel')}
                                            <Infotip pointer="left">{timeFromTooltipText}</Infotip>
                                        </FormItemName>
                                        <TimePicker
                                            id="timeFrom"
                                            value={timeFrom ?? '00:00'}
                                            onChange={(time) => setTimeFrom(time || '00:00')}
                                            style={{ fontWeight: 'lighter' }}
                                            errorMessage={timeFromError}
                                            required
                                            fallbackValue="00:00"
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={6}>
                                    <FormItem htmlFor="timeTo" style={{ position: 'relative' }}>
                                        <FormItemName>
                                            {t('inventory.ruleEditForm.timeToLabel')}
                                            <Infotip pointer="right" maxWidth="25em">
                                                <div style={{ marginBottom: '.7em' }}>{timeToTooltipTopText}</div>
                                                <div>{timeToTooltipBottomText}</div>
                                            </Infotip>
                                        </FormItemName>
                                        <TimePicker
                                            id="timeTo"
                                            value={timeTo ?? '23:59'}
                                            onChange={(time) => setTimeTo(time || '23:59')}
                                            errorMessage={timeToError}
                                            required
                                            fallbackValue="23:59"
                                            style={{ fontWeight: 'lighter' }}
                                        />
                                    </FormItem>
                                </Col>
                            </Row>
                        </Section>
                    </Row>
                    <Row>
                        <Section span={6}>
                            <FormItem htmlFor="capacity" style={{ position: 'relative' }}>
                                <FormItemName>
                                    {t('inventory.ruleEditForm.limitLabel')}
                                    <Infotip pointer="left">{t('inventory.ruleEditForm.limitTooltip')}</Infotip>
                                </FormItemName>
                                <NumberInput
                                    id="capacity"
                                    name="capacity"
                                    integerOnly
                                    min={0}
                                    max={100000}
                                    value={availability !== null ? availability : ''}
                                    placeholder=" "
                                    onChange={(e) => onCapacityChange(e.target.value)}
                                    block
                                />
                            </FormItem>
                        </Section>
                        <Section span={6}>
                            <FormItem htmlFor="priority" style={{ position: 'relative' }}>
                                <FormItemName>
                                    {t('inventory.ruleEditForm.priorityLabel')}
                                    <Infotip pointer="left" maxWidth="30em">
                                        {t('inventory.ruleEditForm.priorityTooltip')}
                                    </Infotip>
                                </FormItemName>
                                <NumberInput
                                    id="priority"
                                    name="priority"
                                    integerOnly
                                    min={2}
                                    max={100}
                                    value={priority !== null ? priority : ''}
                                    placeholder="2"
                                    onChange={(e) =>
                                        e.target.value ? setPriority(Number(e.target.value)) : setPriority(null)
                                    }
                                    block
                                />
                            </FormItem>
                        </Section>
                    </Row>
                    {confirmDeletion && (
                        <Row>
                            <div style={{ fontSize: '.9em', marginBottom: '1.5em' }}>
                                {t('inventory.ruleEditForm.deleteConfirm')}
                            </div>
                        </Row>
                    )}
                    <Row>
                        <Col span={6}>
                            {props.match.params.ruleId !== 'new' && (
                                <ActionButton type="button" kind="destructive" size="large" onClick={onDeleteRule}>
                                    {t('common.delete')}
                                </ActionButton>
                            )}
                            {confirmDeletion && (
                                <ActionButton
                                    size="large"
                                    secondary
                                    onClick={() => setConfirmDeletion(false)}
                                    style={{ marginLeft: '1.5em' }}
                                >
                                    {t('common.cancel')}
                                </ActionButton>
                            )}
                        </Col>
                        <Col span={6}>
                            <ButtonWrapper>
                                <ActionButtonA
                                    size="large"
                                    href={`/account/${props.accountSlug}/products/inventory/item/${props.match.params.inventoryId}`}
                                    secondary
                                    style={{ marginRight: '1.5em' }}
                                    kind="action"
                                >
                                    {t('common.cancel')}
                                </ActionButtonA>
                                <ActionButton id="save-timeSlot" size="large" onClick={onRuleSave}>
                                    {t('common.save')}
                                </ActionButton>
                            </ButtonWrapper>
                        </Col>
                    </Row>
                </ContainerBody>
            )}
        </div>
    )
}

export default withFeatures(withNavigation(InventoryRuleForm))
